<!--
// nuxt-ui/pages/[region]/Teams.vue
-->
<script setup lang="ts">
import type { Region } from "~/src/region/domain/models/Region";
import type { Team } from "~/src/team/domain/models/Team";
import { useCompetitionStore } from "~/nuxt-ui/stores/competitionStore";
import { useWebContext } from "~/nuxt-ui/Shared/composables/webContext";
import { useRegions } from "~/nuxt-ui/Shared/composables/regions";
import { orderTeamsAlphabetically } from "~/src/team/domain/services/teamService";
import { slugify } from "~/src/Shared/utils";
import { useSeasonFilter } from "~/nuxt-ui/Shared/composables/season/seasonFilter";
import CurrentSeasonSliderMatches from "~/nuxt-ui/components/Shared/slider-matches/CurrentSeasonSliderMatches.vue";
import CardTeam from "~/nuxt-ui/components/pages/region-teams/CardTeam.vue";
import HeaderBannerTitle from "~/nuxt-ui/components/Shared/HeaderBannerTitle.vue";
import { getWebContextRegionsData } from "~/src/region/datasources/regionsData";
import SeasonsFilter from "~/nuxt-ui/components/Shared/SeasonsFilter.vue";
import { useSeasons } from "~/nuxt-ui/Shared/composables/season/seasons";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/teams",
    es: "/[region]/equipos",
    de: "/[region]/mannschaften",
    fr: "/[region]/equipes",
    it: "/[region]/squadre",
    pt: "/[region]/times",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations"],
});

const { t } = useI18n();
const localePath = useLocalePath();
const { currentSeasonTeams, region, currentSeason } = useCompetitionStore();
const { isWorldCup, isNations } = useRegions();
const { contextSiteTitle, contextDomain, webContext } = useWebContext();
const { getSeasonTeams } = useSeasons();

const activeTeams = ref<Team[]>(currentSeasonTeams.filter(t => !t.isPlaceholder));

//world cup clubs special functions to divide & filter teams by season
const { seasonId, seasonClusterId } = useSeasonFilter();
const allRegions = ref<Region[]>([]);
const wildcardTeams = ref<Team[]>([]);
const worldCupSectionKey = ref(0);

const isWildcardTeamInSeason = (team: Team, season: number): boolean => {
  return (
    !!team.metaInformation &&
    !!team.metaInformation.wildcard_kwcc &&
    team.metaInformation.wildcard_kwcc.split(",").includes(season.toString())
  );
};

const getRegionTeams = (teams: Team[], region: Region): Team[] => {
  const regionTeams = teams.filter(
    team => !isWildcardTeamInSeason(team, seasonId.value) && team.countryId === region.apiId
  );
  return orderTeamsAlphabetically(regionTeams);
};

const getTeamsData = async (): Promise<void> => {
  try {
    const seasonTeams = await getSeasonTeams(seasonId.value);
    if (seasonTeams.length) {
      activeTeams.value = seasonTeams.filter(t => !t.isPlaceholder);
      wildcardTeams.value = seasonTeams.filter(team => isWildcardTeamInSeason(team, seasonId.value));
    }
  } catch (e) {
    console.error(e);
  } finally {
    worldCupSectionKey.value++;
  }
};

if (isWorldCup) {
  await getTeamsData();
  const regions = getWebContextRegionsData(webContext);
  allRegions.value = regions.filter(r => !r.name.includes("KWC") && r.navVisible);
}
// end world cup clubs special

const teamsItemList = orderTeamsAlphabetically(activeTeams.value).map((team, index) => {
  const url = localePath({
    name: "teams-team-teamName",
    params: { team: team.id, teamName: slugify(team.name) },
  });
  return `{"@type": "ListItem","position": "${index + 1}","item": {"@type" : "SportsTeam","name" : "${team?.name}", "logo": "${team?.logo?.url}", "url":"https://${contextDomain + url}"}}`;
});

useSeoMeta({
  title: t("header.navCompetition.teams"),
  ogTitle: t("header.navCompetition.teams"),
  description: t("meta.description.teams.page", { league: region.leagueTitle || contextSiteTitle }),
  ogDescription: t("meta.description.teams.page", { league: region.leagueTitle || contextSiteTitle }),
});

useHead({
  script: () => [
    {
      type: "application/ld+json",
      innerHTML: `{"@context": "https://schema.org","@type": "ItemList","name": "${contextSiteTitle + " " + t("header.navCompetition.teams")}", "itemListElement": [${teamsItemList}]}`,
    },
  ],
});
</script>

<template>
  <header-banner-title :title="$t('header.navCompetition.teams')"></header-banner-title>

  <section
    v-if="!isWorldCup && !isNations"
    class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 py-8 px-4 md:py-16 md:px-6 kql-w-container"
  >
    <div v-for="(team, i) in orderTeamsAlphabetically(activeTeams)" :key="team.id">
      <card-team
        :to-team-page="
          localePath({
            name: 'teams-team-teamName',
            params: { team: team.id, teamName: slugify(team.name) },
          })
        "
        :team="team"
        :preload="i === 0"
      ></card-team>
    </div>
  </section>

  <section
    v-else-if="isNations"
    class="grid grid-cols-1 md:grid-cols-2 gap-4 py-8 px-4 md:py-16 md:px-6 kql-w-container"
  >
    <div v-for="(team, i) in orderTeamsAlphabetically(activeTeams)" :key="team.id">
      <card-team
        :to-team-page="
          localePath({
            name: 'teams-team-teamName',
            params: { team: team.id, teamName: slugify(team.name) },
          })
        "
        :team="team"
        :preload="i === 0"
      ></card-team>
    </div>
  </section>

  <section v-else-if="isWorldCup" class="pb-8 px-4 md:pb-16 md:px-6 kql-w-container">
    <seasons-filter
      v-model:season-id="seasonId"
      v-model:season-cluster-id="seasonClusterId"
      @change="getTeamsData"
    />

    <div :key="worldCupSectionKey">
      <h2 v-if="wildcardTeams.length" class="text-2xl md:text-3xl font-bold mb-5">Wildcard</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div v-for="(team, i) in orderTeamsAlphabetically(wildcardTeams)" :key="`${team.id}-${i}`">
          <card-team
            :to-team-page="
              seasonId === 37
                ? ''
                : localePath({
                    name: 'teams-team-teamName',
                    params: { team: team.id, teamName: slugify(team.name) },
                  })
            "
            :team="team"
            has-league-title
            is-wildcard-team
          ></card-team>
        </div>
      </div>

      <div v-for="reg in allRegions" :key="reg.id">
        <h2
          v-if="getRegionTeams(activeTeams, reg).length"
          class="text-2xl md:text-3xl font-bold mb-5 mt-8 md:mt-12"
        >
          {{ reg.leagueTitle }}
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div v-for="(team, i) in getRegionTeams(activeTeams, reg)" :key="`${team.id}-${i}`">
            <card-team
              :to-team-page="
                localePath({
                  name: 'teams-team-teamName',
                  params: { team: team.id, teamName: slugify(team.name) },
                })
              "
              :team="team"
              has-league-title
            ></card-team>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="py-8" v-if="!isWorldCup && !isNations">
    <current-season-slider-matches section-title></current-season-slider-matches>
  </section>
</template>

<style scoped></style>
